import * as React from 'react'
import { CalendarCheck, CloudOff, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { toErrorMessage } from '@/helpers/logger'
import { VISIT_KINDS, type VisitKind } from '@lib/enums/visit-kind.enum'
import type { SiteVisits } from '@/site-visits/useSiteVisits'
import {
  isSameVisit,
  todayLocal,
  type LocalVisit,
  type VisitCovers,
} from '@/site-visits/visitStore'

/** "Thu, Oct 16", or with the year when it is not this one. A day, not an instant. */
export function formatVisitDay(day: string, now: Date = new Date()): string {
  const date = new Date(`${day}T00:00:00Z`)
  const thisYear = date.getUTCFullYear() === now.getFullYear()
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    ...(thisYear
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }),
  })
}

/** "Trees", "Irrigation", or "Trees and irrigation". */
export function visitCovers(
  visit: Pick<LocalVisit, 'coversTrees' | 'coversIrrigation'>,
): string {
  if (visit.coversTrees && visit.coversIrrigation) return 'Trees and irrigation'
  return visit.coversTrees ? 'Trees' : 'Irrigation'
}

/**
 * Whether what a visit found can be changed on this screen without asking:
 * it was started today and is still open, or somebody chose to carry on with
 * it. A visit is never locked (see docs/site-visits.md), but one from an
 * earlier day, finished or left open, does not take findings until somebody
 * says so: a technician back a year later must not file this year's problems
 * on last year's visit by default.
 */
export function canRecordIn(
  visit: LocalVisit | null,
  unlockedId: string | null,
  today: string = todayLocal(),
): boolean {
  if (visit === null) return false
  if (unlockedId !== null && isSameVisit(visit, unlockedId)) return true
  return visit.finishedAt === null && visit.startedOn === today
}

interface VisitBarProps {
  /** The survey this bar sits on. */
  kind: VisitKind
  visits: SiteVisits
  /** The visit the survey is working in: the newest covering its kind. */
  working: LocalVisit | null
  /** The finished visit somebody chose to edit, if any. */
  unlockedId: string | null
  onUnlock: (visitId: string) => void
  isOnline: boolean
  /**
   * Show an earlier visit instead of the newest, or null for the newest
   * again. Left out, the bar offers no other visit.
   */
  onView?: (visitId: string | null) => void
  /** The visit shown is an earlier one than the newest, chosen with `onView`. */
  viewingEarlier?: boolean
}

/**
 * Which visit the survey is recording into, under the screen's header, and
 * the two things to do about it: start one, or say it is done.
 *
 * Starting is explicit (the operator's call, 2026-10-16): problems and work
 * are a visit's, so the screen asks for one before anything found can be
 * written, rather than quietly filing a finding on last year's.
 */
export function VisitBar({
  kind,
  visits,
  working,
  unlockedId,
  onUnlock,
  isOnline,
  onView,
  viewingEarlier = false,
}: VisitBarProps) {
  const [choosing, setChoosing] = React.useState(false)
  const all = visits.all(kind)
  const [kinds, setKinds] = React.useState<VisitCovers>({
    trees: kind === 'trees',
    irrigation: kind === 'irrigation',
  })
  const [isFinishing, setIsFinishing] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  function openChooser(): void {
    setKinds({
      trees: kind === 'trees',
      irrigation: kind === 'irrigation',
    })
    setError(null)
    setChoosing(true)
  }

  function handleStart(): void {
    const visit = visits.start(kinds)
    // Today's visit, finished earlier today, is the same visit: back to it.
    if (visit.finishedAt !== null) onUnlock(visit.id)
    // And it is the one shown, whatever earlier visit was being looked at.
    onView?.(null)
    setChoosing(false)
  }

  async function handleFinish(visit: LocalVisit): Promise<void> {
    setError(null)
    setIsFinishing(true)
    try {
      await visits.setFinished(visit.id, true)
    } catch (caught) {
      setError(toErrorMessage(caught))
    } finally {
      setIsFinishing(false)
    }
  }

  function renderChooser(): React.ReactNode {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-secondary-text">This visit covers</span>
        {VISIT_KINDS.map((choice) => (
          <label
            key={choice}
            className={cn(
              'flex h-9 cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-xs',
              kinds[choice]
                ? 'border-strong bg-subtle text-primary-text'
                : 'border-default text-secondary-text',
            )}
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5"
              checked={kinds[choice]}
              onChange={(event) => {
                setKinds((current) => ({
                  ...current,
                  [choice]: event.target.checked,
                }))
              }}
            />
            {choice === 'trees' ? 'Trees' : 'Irrigation'}
          </label>
        ))}
        <div className="ml-auto flex gap-1.5">
          <Button
            variant="ghost"
            className="h-9"
            onClick={() => {
              setChoosing(false)
            }}
          >
            Cancel
          </Button>
          <Button
            className="h-9"
            disabled={!kinds.trees && !kinds.irrigation}
            onClick={handleStart}
          >
            Start
          </Button>
        </div>
      </div>
    )
  }

  function renderState(): React.ReactNode {
    if (working === null) {
      return (
        <>
          <p className="min-w-0 text-xs text-secondary-text">
            No visit yet. Start one to record what you find.
          </p>
          <Button className="ml-auto h-9 shrink-0" onClick={openChooser}>
            Start a visit
          </Button>
        </>
      )
    }

    const day = formatVisitDay(working.startedOn)
    if (!canRecordIn(working, unlockedId)) {
      const finished = working.finishedAt !== null
      return (
        <>
          <p className="min-w-0 truncate text-xs text-secondary-text">
            {finished
              ? `${viewingEarlier ? 'Visit' : 'Last visit'} · ${day} · finished`
              : `The visit started ${day} is still open`}
          </p>
          <div className="ml-auto flex shrink-0 gap-1.5">
            <Button
              variant="outline"
              className="h-9"
              onClick={() => {
                onUnlock(working.id)
              }}
            >
              {finished ? 'Edit it' : 'Continue it'}
            </Button>
            <Button className="h-9" onClick={openChooser}>
              Start a visit
            </Button>
          </div>
        </>
      )
    }

    return (
      <>
        <p className="flex min-w-0 items-center gap-1.5 text-xs">
          <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-secondary-text" />
          <span className="truncate">
            <span className="font-medium">Visit · {day}</span>
            <span className="text-secondary-text">
              {' '}
              · {visitCovers(working)}
            </span>
          </span>
          {working.pending ? (
            <span
              className="flex shrink-0 items-center gap-1 text-muted-text"
              title={
                visits.sendError ??
                'Started on this phone. Sent when there is signal; what you record waits for it.'
              }
            >
              <CloudOff className="h-3 w-3" />
              Not sent yet
            </span>
          ) : null}
        </p>
        <div className="ml-auto flex shrink-0 gap-1.5">
          {working.finishedAt === null ? (
            <Button
              variant="outline"
              className="h-9"
              disabled={!isOnline || working.pending || isFinishing}
              title={
                isOnline ? undefined : 'Finishing a visit needs a connection.'
              }
              onClick={() => {
                void handleFinish(working)
              }}
            >
              {isFinishing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Finish visit
            </Button>
          ) : null}
        </div>
      </>
    )
  }

  /**
   * Which visit is shown, when there is more than one: the newest, or an
   * earlier one to read or edit what it found (docs/site-visits.md).
   */
  function renderSwitcher(): React.ReactNode {
    if (onView === undefined || all.length < 2 || working === null) return null
    const newestId = all[0]?.id
    return (
      <select
        aria-label="Visit shown"
        value={working.id}
        // Each visit's findings come from the server: switching away offline
        // would leave the one being worked in unreadable until signal.
        disabled={!isOnline}
        title={
          isOnline ? undefined : 'Showing another visit needs a connection.'
        }
        className="h-9 shrink-0 rounded-md border border-default bg-surface px-2 text-xs text-primary-text disabled:opacity-60"
        onChange={(event) => {
          const { value } = event.target
          onView(value === newestId ? null : value)
        }}
      >
        {all.map((visit, index) => (
          <option key={visit.id} value={visit.id}>
            {index === 0
              ? `Newest · ${formatVisitDay(visit.startedOn)}`
              : formatVisitDay(visit.startedOn)}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div className="z-20 shrink-0 space-y-1 border-b border-default bg-surface px-3 py-1.5">
      <div className="flex min-h-9 flex-wrap items-center gap-2">
        {choosing ? (
          renderChooser()
        ) : (
          <>
            {renderState()}
            {renderSwitcher()}
          </>
        )}
      </div>
      {viewingEarlier && working !== null && onView !== undefined ? (
        <p className="text-xs text-secondary-text">
          Drawn as the site stood on {formatVisitDay(working.startedOn)}. The
          site itself is changed on the newest visit.{' '}
          <button
            type="button"
            className="font-medium text-primary-text underline underline-offset-2"
            onClick={() => {
              onView(null)
            }}
          >
            Back to the newest
          </button>
        </p>
      ) : null}
      {error === null ? null : (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}
